import { useEffect, useState } from "react";
import { ownerApi, formatDetail } from "../api";
import { formatCT } from "../lib/time";
import { useOwnerAuth } from "../OwnerAuthContext";
import { Loader2, Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "@/lib/toastBanner";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";

export default function Owners() {
  const { owner: me } = useOwnerAuth();
  const [list, setList] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await ownerApi.get("/owners");
      setList(data);
    } catch {
      setList([]); toast.error("Failed to load Owners");
    }
  };
  useEffect(() => { load(); }, []);

  const performDelete = async () => {
    const o = deleteTarget;
    if (!o) return;
    try {
      await ownerApi.delete(`/owners/${o.id}`);
      toast.success("Owner deleted");
      if (o.id === me?.id) window.location.href = "/owner/login";
      else load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Delete failed");
      throw e;
    }
  };

  return (
    <div className="space-y-6" data-testid="owner-users">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#f1fcff]">Owners</h1>
          <p className="text-sm text-[#ebeef5]/70 mt-1">Platform-level administrators. All Owners have identical rights.</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="bg-[#10b7c0] hover:bg-[#0ea2aa] text-black text-xs font-semibold px-3 py-2 rounded-md flex items-center gap-1.5"
          data-testid="owner-users-create-btn"
        >
          <Plus className="h-3.5 w-3.5" /> Add Owner
        </button>
      </div>

      {list === null ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-[#10b7c0]" /></div>
      ) : (
        <div className="bg-[#0a1530] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="owner-users-table">
            <thead className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-[#ebeef5]/70">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Email</th>
                <th className="text-left px-5 py-3 font-semibold">Created</th>
                <th className="text-left px-5 py-3 font-semibold">Last login</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {list.map((o) => (
                <tr key={o.id} className="hover:bg-white/[0.02]" data-testid={`owner-user-row-${o.id}`}>
                  <td className="px-5 py-3">
                    <span className="font-semibold text-[#f1fcff]">{o.email}</span>
                    {o.id === me?.id && <span className="ml-2 text-[10px] uppercase font-semibold text-[#10b7c0]">(you)</span>}
                  </td>
                  <td className="px-5 py-3 text-xs text-[#ebeef5]/70">{formatCT(o.created_at)}</td>
                  <td className="px-5 py-3 text-xs text-[#ebeef5]/70">{o.last_login_at ? formatCT(o.last_login_at) : "never"}</td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => setResetFor(o)}
                      className="text-xs font-semibold text-[#ebeef5]/70 hover:text-[#10b7c0] mr-3 inline-flex items-center gap-1"
                      data-testid={`owner-user-reset-${o.id}`}
                    >
                      <KeyRound className="h-3 w-3" /> Reset pwd
                    </button>
                    <button
                      onClick={() => setDeleteTarget(o)}
                      className="text-xs font-semibold text-red-400 hover:text-red-300 inline-flex items-center gap-1"
                      data-testid={`owner-user-delete-${o.id}`}
                    >
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && <CreateOwnerDialog onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
      {resetFor && <ResetPasswordDialog target={resetFor} onClose={() => setResetFor(null)} />}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        tier={3}
        realm="owner"
        entityName={deleteTarget?.email}
        entityType="owner"
        entityId={deleteTarget?.id}
        description={
          deleteTarget?.id === me?.id
            ? "You are deleting your own Owner account. Enter your credentials to confirm — you'll be signed out immediately."
            : undefined
        }
        onConfirm={performDelete}
      />
    </div>
  );
}

function CreateOwnerDialog({ onClose, onCreated }) {
  const [email, setEmail] = useState(""); const [pw, setPw] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await ownerApi.post("/owners", { email: email.trim(), password: pw });
      toast.success("Owner created");
      onCreated();
    } catch (e2) {
      setErr(formatDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Add Owner">
      <form onSubmit={submit} data-testid="owner-users-create-dialog">
        <Field label="Email" type="email" required value={email} onChange={setEmail} testid="new-owner-email" />
        <Field label="Temporary password" type="password" required value={pw} onChange={setPw} testid="new-owner-password" />
        {err && <p className="text-xs text-red-400 mb-3" data-testid="owner-users-create-error">{err}</p>}
        <div className="flex gap-2 justify-end mt-4">
          <button type="button" onClick={onClose} className="text-xs font-semibold px-3 py-2 rounded-md border border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff]">Cancel</button>
          <button type="submit" disabled={busy} className="text-xs font-semibold px-3 py-2 rounded-md bg-[#10b7c0] text-black hover:bg-[#0ea2aa] disabled:opacity-50 flex items-center gap-1.5" data-testid="owner-users-create-submit">
            {busy && <Loader2 className="h-3 w-3 animate-spin" />} Create
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ResetPasswordDialog({ target, onClose }) {
  const [pw, setPw] = useState(""); const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await ownerApi.post(`/owners/${target.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${target.email}`);
      onClose();
    } catch (e2) {
      setErr(formatDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={`Reset password — ${target.email}`}>
      <form onSubmit={submit} data-testid="owner-users-reset-dialog">
        <p className="text-xs text-[#ebeef5]/70 mb-3">Deliver this password to the Owner out-of-band.</p>
        <Field label="New password" type="password" required value={pw} onChange={setPw} testid="reset-new-password" />
        {err && <p className="text-xs text-red-400 mb-3">{err}</p>}
        <div className="flex gap-2 justify-end mt-4">
          <button type="button" onClick={onClose} className="text-xs font-semibold px-3 py-2 rounded-md border border-white/10 text-[#ebeef5]/70 hover:text-[#f1fcff]">Cancel</button>
          <button type="submit" disabled={busy} className="text-xs font-semibold px-3 py-2 rounded-md bg-[#10b7c0] text-black hover:bg-[#0ea2aa] disabled:opacity-50 flex items-center gap-1.5" data-testid="owner-users-reset-submit">
            {busy && <Loader2 className="h-3 w-3 animate-spin" />} Reset
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-[#0a1530] border border-white/10 rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading font-bold text-lg text-[#f1fcff] mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function Field({ label, type = "text", required, value, onChange, testid }) {
  return (
    <div className="mb-3">
      <label className="block text-[10px] uppercase tracking-wider font-semibold text-[#ebeef5]/70 mb-1">
        {label}{required && <span className="text-red-400"> *</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-[#040b1d] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#f1fcff] focus:outline-none focus:border-[#10b7c0]/50"
      />
    </div>
  );
}
